/** @format */

import {useMemo, useState} from 'react';

import * as R from 'ramda';
import Alert from '@mui/material/Alert';
import {Box, CircularProgress, Typography} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {createStyles, makeStyles} from '@mui/styles';
import {Trans, useTranslation} from 'react-i18next';
import {add, format} from 'date-fns';

import BackHeader from 'components/BackHeader';
import CheckGraph from 'components/graphs/CheckGraph';
import PrimarySelect from 'components/selects/PrimarySelect';
import {useCompanyContext} from 'contexts/CompanyContext';

import BudgetChecksTable from './BudgetChecksTable';
import {useGetCurrentUserQuery} from './graphql/__generated__';
import {GraphBudgetScoreType, MenuOption} from './types';

const MENU_OPTIONS: MenuOption[] = [
  {value: '7_DAYS', days: 7},
  {value: '15_DAYS', days: 15},
  {value: '30_DAYS', days: 30},
];

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    loadingContainer: {
      alignItems: 'center',
      display: 'flex',
      height: 441,
      justifyContent: 'center',
      width: '100%',
    },
  }),
);

const Graphs = () => {
  const classes = useStyles();

  const {t} = useTranslation();

  const {currentAdType} = useCompanyContext();

  const [menuOption, setMenuOption] = useState<MenuOption>(MENU_OPTIONS[0]);

  const now = new Date();
  const startDate = add(now, {days: -(menuOption.days - 1)});
  const {loading, error, data} = useGetCurrentUserQuery({
    variables: {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(now, 'yyyy-MM-dd'),
    },
  });

  const sortedGraphChecks = useMemo(() => {
    if (!data?.currentUser?.currentCompany) return [];

    const {graphChecks} = data.currentUser.currentCompany;
    return R.sortBy(v => v.collectedOn, graphChecks);
  }, [data]);

  const sortedBudgetScoreChecks = useMemo(() => {
    if (!data?.currentUser?.currentCompany) return [];

    const {graphBudgetScores} = data.currentUser.currentCompany;
    const byCollectedOn = R.descend<GraphBudgetScoreType>(R.prop('collectedOn'));

    return R.sort(byCollectedOn, graphBudgetScores);
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser?.currentCompany) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSelectPeriod = (value: string) => {
    const newMenuOption = MENU_OPTIONS.find(v => v.value === value);
    if (newMenuOption) {
      setMenuOption(newMenuOption);
    }
  };

  const daysOptions = MENU_OPTIONS.map(v => ({
    value: v.value,
    text: t('За {{daysCount}} дней', {daysCount: v.days}),
  }));

  const {uuid, sensorExclusions, lastChecks} = data.currentUser.currentCompany;

  return (
    <>
      <Box mb={3}>
        <BackHeader />
      </Box>

      <Box className={classes.titleContainer} alignItems='baseline' display='flex'>
        <Typography variant='h1'>
          <Trans>Динамика показателей</Trans>
        </Typography>
        <Box ml={5}>
          <PrimarySelect
            options={daysOptions}
            value={menuOption.value}
            onSelect={handleSelectPeriod}
          />
        </Box>
      </Box>

      <Box mt={5}>
        <Typography variant='h2'>
          <Trans>Срабатывание сенсоров</Trans>
        </Typography>
        <CheckGraph graphChecks={sortedGraphChecks} />
      </Box>
      <BudgetChecksTable
        uuid={uuid}
        userUuid={data.currentUser.uuid}
        lang={data.currentUser.lang || 'RU'}
        sensorExclusions={[...sensorExclusions]}
        lastChecks={[...lastChecks]}
        budgetScoreChecks={sortedBudgetScoreChecks}
        graphChecks={sortedGraphChecks}
        currentAdType={currentAdType}
      />
    </>
  );
};

export default Graphs;
